import {
	Component,
	EventEmitter,
	HostBinding,
	Input,
	Output
} from '@angular/core';
import {
	ToastV2Action,
	ToastV2TypeEnum
} from '@brand-lolly/app/toast-v2/models/toast-v2.model';
import { NgIf } from '@angular/common';
import { IconComponent, TypographyComponent } from 'components';

@Component({
	standalone: true,
	selector: 'app-toast-item',
	template: `
		<div class="glass">
			<app-icon type="svg" [size]="26">
				{{ 'toast_' + toastType.toLowerCase() }}
			</app-icon>
		</div>

		<div class="body">
			<typography variant="p/base_semibold">{{ toastTitle }}</typography>
			<typography variant="p/sm_regular">{{ toastMessage }}</typography>

			<button
				*ngIf="toastAction"
				type="button"
				class="action-button"
				(click)="actionInvoked.emit()"
			>
				<typography variant="p/sm_semibold">
					{{ toastAction.label }}
				</typography>
			</button>
		</div>

		<button
			type="button"
			class="close-button"
			aria-label="Close"
			(click)="closeToast.emit()"
		>
			<svg class="circle-wrapper" viewBox="0 0 64 64">
				<circle cx="32" cy="32" r="31" />
			</svg>

			<app-icon type="svg" [size]="16" color="#fff">clear</app-icon>
		</button>
	`,
	imports: [NgIf, TypographyComponent, IconComponent],
	styles: [
		`
			:host {
				--primary-error: #e14128;
				--secondary-error: #e27171;

				--primary-warning: #e49130;
				--secondary-warning: #e2ad6f;

				--primary-success: #30ad43;
				--secondary-success: #5eb961;

				--primary-info: #0a97d6;
				--secondary-info: #6bbbdf;

				--duration: initial;

				&.error {
					--primary: var(--primary-error);
					--secondary: var(--secondary-error);
				}

				&.warning {
					--primary: var(--primary-warning);
					--secondary: var(--secondary-warning);
				}

				&.success {
					--primary: var(--primary-success);
					--secondary: var(--secondary-success);
				}

				&.info {
					--primary: var(--primary-info);
					--secondary: var(--secondary-info);
				}

				display: flex;
				max-width: 28.125rem;
				width: 100%;
				padding: 0.875rem 1.75rem;
				align-items: center;
				gap: 0.75rem;
				border-radius: 1.25rem;
				background: green;
				background: var(--primary);

				.glass {
					width: 2.75rem;
					height: 2.75rem;
					border-radius: 50%;
					background: linear-gradient(
						315deg,
						rgba(255, 255, 255, 0.2),
						rgba(255, 255, 255, 0.05)
					);
					backdrop-filter: blur(10px);
					border: 1px solid rgba(255, 255, 255, 0.3);
					display: flex;
					align-items: center;
					justify-content: center;
				}

				.body {
					display: flex;
					flex: 1;
					flex-direction: column;

					typography {
						color: #fff;
					}

					.action-button {
						align-self: flex-start;
						margin-top: 0.375rem;
						padding: 0.25rem 0.75rem;
						border: 1px solid rgba(255, 255, 255, 0.6);
						border-radius: 0.5rem;
						background: transparent;
						cursor: pointer;
						transition: background 150ms;

						&:hover {
							background: rgba(255, 255, 255, 0.18);
						}
					}
				}

				.close-button {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 2rem;
					height: 2rem;
					padding: 0;
					border: none;
					font: inherit;
					background: transparent;
					transition: background 150ms;
					position: relative;
					border-radius: 50%;
					outline: 1px solid var(--secondary);
					cursor: pointer;

					.circle-wrapper {
						transform: rotate(-90deg);
						position: absolute;
						inset: -1px;

						circle {
							fill: none;
							stroke: #fff;
							stroke-width: 2px;
							stroke-dasharray: 194;
							stroke-dashoffset: 194;
							transform-origin: center;
							animation: fillCircle var(--duration) linear
								forwards;
						}
					}

					&:hover {
						background: var(--secondary);
					}
				}
			}

			@keyframes fillCircle {
				to {
					stroke-dashoffset: 0;
				}
			}
		`
	]
})
export class ToastItemComponent {
	@Input() toastType: ToastV2TypeEnum = ToastV2TypeEnum.INFO;
	@Input() toastTitle: string = '';
	@Input() toastMessage: string = '';
	@Input() toastDuration: number = 0;
	/** Absent for every in-app toast; present on a server-originated one. */
	@Input() toastAction: ToastV2Action | null = null;

	@Output() closeToast = new EventEmitter<void>();
	@Output() actionInvoked = new EventEmitter<void>();

	@HostBinding('class')
	get toastTypeClass() {
		return this.toastType.toLowerCase();
	}

	@HostBinding('style.--duration')
	get duration() {
		return `${this.toastDuration}ms`;
	}
}
